import * as Phaser from 'phaser';
import { PhaserAnimationManager } from '../../PhaserAnimationManager';
import { MovementAnimationSync } from '../../MovementAnimationSync';

export interface ISceneAdapter {
  idle(agentId: string, params?: { spawn?: boolean; gridX?: number; gridY?: number; color?: string; initialHealth?: { current: number; max: number } }): void;
  walk(agentId: string, to: { x: number; y: number }, onComplete?: () => void): void;
  hurt(agentId: string, params?: any): void;
  use(agentId: string, params?: { itemType?: string }): void;
  rush(agentId: string, params?: any): void;
  atk(agentId: string, params?: any): void;
  die(agentId: string, params?: any): void;
  onComplete(agentId: string, cb: () => void): void;
  setEquippedWeapon?(agentId: string, weaponType: string): void;
  removeResourceSprite?(itemId: string): void;

  // 🔥 SafeZone动画方法
  showSafeZoneWarning?(centerX: number, centerY: number, fromRadius: number, toRadius: number): void;
  animateSafeZoneContraction?(centerX: number, centerY: number, fromRadius: number, toRadius: number, duration: number): void;
  updateSafeZoneDisplay?(centerX: number, centerY: number, radius: number): void;
}

export class SceneAdapter implements ISceneAdapter {
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly pma: PhaserAnimationManager
  ) {}

  idle(agentId: string, params?: { spawn?: boolean; gridX?: number; gridY?: number; color?: string; initialHealth?: { current: number; max: number } }): void {
    // 🔧 迁移：处理AGENT_SPAWNED的创建逻辑
    if (params?.spawn && typeof params.gridX === 'number' && typeof params.gridY === 'number') {
      if (!params.color) {
        throw new Error(`SceneAdapter.idle(): 创建Agent ${agentId} 时缺少必需的颜色参数`);
      }
      const color = params.color;
      console.log(`🎭 SceneAdapter: 创建/复用Agent ${agentId} at grid(${params.gridX}, ${params.gridY}) color: ${color}`);

      // 若已存在旧容器，先销毁，避免重复残留
      try {
        const anyPma: any = this.pma as any;
        const existing = anyPma.agentContainers?.get(agentId);
        if (existing) {
          // 停止In-World分层动画的计时器，避免已销毁容器上的Sprite被继续setTexture
          try { const anyScene: any = this.scene as any; anyScene?.inWorldAnimationPlayer?.stopAnimation?.(agentId); } catch {}
          try { existing.destroy(true); } catch {}
          try { anyPma.agentContainers.delete(agentId); } catch {}
          try { const spr = anyPma.agentSprites?.get(agentId); if (spr) spr.destroy(); anyPma.agentSprites?.delete(agentId); } catch {}
          try { const w = anyPma.weaponSprites?.get(agentId); if (w) w.destroy(); anyPma.weaponSprites?.delete(agentId); } catch {}
          try { anyPma.equippedTypes?.delete(agentId); } catch {}
        }
      } catch {}

      // 创建Agent容器和精灵（迁移自BattleScene.addAgent），传递颜色信息
      const agentContainer = this.pma.addAgent(agentId, params.gridX, params.gridY, color);

      if (agentContainer) {
        // 获取实际的Container对象来添加UI元素
        const container = this.pma.agentContainers.get(agentId);
        if (container) {
          // 添加Agent ID标签（迁移自BattleScene.addAgent）
          const agentText = this.scene.add.text(0, -35, agentId, {
            fontSize: '8px',
            color: '#ffffff',
            align: 'center',
            fontFamily: 'Arial'
          });
          agentText.setOrigin(0.5, 0.5);
          container.add(agentText);

          // 🆕 集成血条到容器（仅创建，不进行任何占位绘制，等待后端健康状态驱动）
          try {
            const battleScene: any = this.scene as any;
            if (typeof battleScene.createOrGetAgentHealthBar === 'function') {
              battleScene.createOrGetAgentHealthBar(agentId);
            }
            // 开场即绘制地图层血条（若有起始快照）
            if (params?.initialHealth && typeof battleScene.updateAgentHealthBar === 'function') {
              battleScene.updateAgentHealthBar(agentId, params.initialHealth);
            }
          } catch {}

          console.log(`✅ SceneAdapter: 成功创建Agent ${agentId} 容器和UI`);
        }
      }

      // 初始化MovementSync当前位置，避免首次移动起点为(0,0)的日志错觉
      try { (this.pma as any).updateAgentPosition(agentId, params.gridX, params.gridY); } catch {}
    }

    // 播放idle动画
    this.pma.playAnimation(agentId, 'idle');
  }
  walk(agentId: string, to: { x: number; y: number }, onComplete?: () => void): void {
    // 移动完成回调直接传递给PhaserAnimationManager
    this.pma.moveAgentWithAnimation(agentId, to.x, to.y, onComplete);
  }
  hurt(agentId: string): void { this.playOnceThenIdle(agentId, 'hurt'); }
  use(agentId: string, params?: { itemType?: string }): void {
    const itemType = params?.itemType || 'LARGE_MEDKIT';
    console.log(`🧪 SceneAdapter: ${agentId} using item ${itemType}`);
    this.playOnceThenIdle(agentId, 'use');
  }
  rush(agentId: string): void { this.playOnceThenIdle(agentId, 'rush'); }
  atk(agentId: string): void { this.playOnceThenIdle(agentId, 'atk'); }
  die(agentId: string): void {
    // 播放死亡动画，等待完成后统一清理（由 PMA 主持，消除残影）
    this.pma.playAnimation(agentId, 'die');
    this.pma.onAnimationComplete(agentId, () => {
      // 先停掉分层动画播放器（如存在），避免残余计时器继续推进
      try { const anyScene: any = this.scene as any; anyScene?.inWorldAnimationPlayer?.stopAnimation?.(agentId); } catch {}
      try { (this.pma as any).removeAgent?.(agentId); } catch {}
      try { (this.scene as any).removeAgentSprite?.(agentId); } catch {}
    });
  }
  setEquippedWeapon?(agentId: string, weaponType: string): void {
    try { (this.pma as any).setEquippedWeapon?.(agentId, weaponType); } catch {}
  }
  removeResourceSprite?(itemId: string): void {
    try {
      // 优先使用 ResourceSystem 的统一管理，命中其内部 Map 立即移除视觉
      const anyScene: any = this.scene as any;
      const rs = anyScene?.resourceSystem;
      if (rs && typeof rs.removeResource === 'function') {
        try { rs.removeResource(itemId); return; } catch {}
      }
      // 兜底：调用 BattleScene 的 legacy 方法
      const battleScene: any = this.scene as any;
      if (typeof battleScene.removeResourceSprite === 'function') {
        battleScene.removeResourceSprite(itemId);
      }
    } catch {}
  }

  onComplete(agentId: string, cb: () => void): void {
    this.pma.onAnimationComplete(agentId, cb);
  }

  private playOnceThenIdle(agentId: string, anim: 'hurt'|'use'|'rush'|'atk') {
    const ok = this.pma.playAnimation(agentId, anim);
    if (ok) {
      this.pma.onAnimationComplete(agentId, () => {
        try { this.pma.playAnimation(agentId, 'idle'); } catch {}
      });
    }
  }

  // 🔥 SafeZone动画方法实现
  showSafeZoneWarning?(centerX: number, centerY: number, fromRadius: number, toRadius: number): void {
    console.log(`⚠️ SceneAdapter: SafeZone警告 - 中心(${centerX}, ${centerY}), 从半径${fromRadius}→${toRadius}`);

    // 检查scene是否有showSafeZoneWarning方法
    const battleScene = this.scene as any;
    if (typeof battleScene.showSafeZoneWarning === 'function') {
      // 调用BattleScene现有的SafeZone警告方法
      battleScene.showSafeZoneWarning(centerX, centerY, fromRadius, toRadius);
    } else {
      console.warn('⚠️ SceneAdapter: BattleScene没有showSafeZoneWarning方法');
    }
  }

  animateSafeZoneContraction?(centerX: number, centerY: number, fromRadius: number, toRadius: number, duration: number): void {
    console.log(`🔄 SceneAdapter: SafeZone收缩动画 - 中心(${centerX}, ${centerY}), ${fromRadius}→${toRadius}, 持续${duration}ms`);

    // 检查scene是否有animateSafeZoneShrink方法
    const battleScene = this.scene as any;
    if (typeof battleScene.animateSafeZoneShrink === 'function') {
      // 调用BattleScene现有的SafeZone收缩动画方法
      battleScene.animateSafeZoneShrink(centerX, centerY, fromRadius, toRadius, duration);
    } else {
      console.warn('🔄 SceneAdapter: BattleScene没有animateSafeZoneShrink方法');
    }
  }

  updateSafeZoneDisplay?(centerX: number, centerY: number, radius: number): void {
    console.log(`🔄 SceneAdapter: SafeZone显示更新 - 中心(${centerX}, ${centerY}), 半径${radius}`);

    // 检查scene是否有updateSafeZone方法
    const battleScene = this.scene as any;
    if (typeof battleScene.updateSafeZone === 'function') {
      // 调用BattleScene现有的SafeZone更新方法
      battleScene.updateSafeZone(centerX, centerY, radius);
    } else {
      console.warn('🔄 SceneAdapter: BattleScene没有updateSafeZone方法');
    }
  }
}
